import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ClassroomManagementCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ClassroomEnvironmentStep: React.FC<
  StepComponentProps<ClassroomManagementCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomGoal, setShowCustomGoal] = useState(false);

  const managementGoalOptions = [
    {
      id: "ensure_silence",
      emoji: "🤫",
      label: t("classroomManagement.managementGoal.ensureSilence"),
      desc: t("classroomManagement.managementGoal.ensureSilenceDesc"),
    },
    {
      id: "increase_participation",
      emoji: "🙋",
      label: t("classroomManagement.managementGoal.increaseParticipation"),
      desc: t("classroomManagement.managementGoal.increaseParticipationDesc"),
    },
    {
      id: "develop_positive_behavior",
      emoji: "✨",
      label: t("classroomManagement.managementGoal.developPositiveBehavior"),
      desc: t("classroomManagement.managementGoal.developPositiveBehaviorDesc"),
    },
    {
      id: "improve_self_regulation",
      emoji: "🧘",
      label: t("classroomManagement.managementGoal.improveSelfRegulation"),
      desc: t("classroomManagement.managementGoal.improveSelfRegulationDesc"),
    },
  ];

  const classSizeOptions = [
    {
      id: "small",
      emoji: "👥",
      label: t("classroomManagement.classSize.small"),
      desc: t("classroomManagement.classSize.smallDesc"),
    },
    {
      id: "medium",
      emoji: "👫",
      label: t("classroomManagement.classSize.medium"),
      desc: t("classroomManagement.classSize.mediumDesc"),
    },
    {
      id: "large",
      emoji: "👬",
      label: t("classroomManagement.classSize.large"),
      desc: t("classroomManagement.classSize.largeDesc"),
    },
    {
      id: "very_large",
      emoji: "👭",
      label: t("classroomManagement.classSize.veryLarge"),
      desc: t("classroomManagement.classSize.veryLargeDesc"),
    },
  ];

  const handleGoalSelect = (goalId: string) => {
    onDataChange({ classroomManagementGoal: goalId });
  };

  const handleClassSizeSelect = (sizeId: string) => {
    onDataChange({ classSize: sizeId });
  };

  const handleCustomGoalToggle = () => {
    const currentGoal = data.classroomManagementGoal;
    const hasCustomGoal =
      currentGoal &&
      !managementGoalOptions.some((option) => option.id === currentGoal);

    if (hasCustomGoal || showCustomGoal) {
      onDataChange({ classroomManagementGoal: undefined });
      setShowCustomGoal(false);
    } else {
      setShowCustomGoal(true);
    }
  };

  const handleCustomGoalChange = (value: string) => {
    if (value.trim()) {
      onDataChange({ classroomManagementGoal: value.trim() });
    } else {
      onDataChange({ classroomManagementGoal: undefined });
    }
  };

  const isCustomGoalSelected = () => {
    const currentGoal = data.classroomManagementGoal;
    return (
      showCustomGoal ||
      (currentGoal &&
        !managementGoalOptions.some((option) => option.id === currentGoal))
    );
  };

  const getCustomGoalValue = () => {
    const currentGoal = data.classroomManagementGoal;
    if (
      currentGoal &&
      !managementGoalOptions.some((option) => option.id === currentGoal)
    ) {
      return currentGoal;
    }
    return "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("classroomManagement.steps.classroomEnvironment.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("classroomManagement.steps.classroomEnvironment.description")}
        </p>
      </div>

      {/* Classroom Management Goal */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("classroomManagement.form.managementGoal")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("classroomManagement.form.managementGoalDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {managementGoalOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.classroomManagementGoal === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleGoalSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.classroomManagementGoal === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.classroomManagementGoal === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Goal Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomGoalSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomGoalToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isCustomGoalSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomGoalSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("classroomManagement.managementGoal.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("classroomManagement.managementGoal.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Goal Input */}
        {isCustomGoalSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("classroomManagement.form.customGoal")}
            </label>
            <input
              type="text"
              value={getCustomGoalValue()}
              onChange={(e) => handleCustomGoalChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("classroomManagement.form.customGoalPlaceholder")}
            />
          </div>
        )}
      </div>

      {/* Class Size */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("classroomManagement.form.classSize")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {classSizeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.classSize === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleClassSizeSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.classSize === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.classSize === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
